// src/utils/categoria.ts
//
// Reglas de sexo por categoría. Única fuente de verdad: el helper
// getDefaultGenderForCategory y la garantía ensureSexoFromCategoria.
//
// Solo los TERNEROS pueden quedar sin sexo (undefined): el usuario elige
// ternero (M) vs ternera (H), relevante para precios (animalValuationService).
// TODA otra categoría tiene sexo FIJO y automático, no seteable por el usuario:
//   TORO / NOVILLO / M.E.J  → M (macho)
//   VACA / VAQUILLONA       → H (hembra)

import { Enums } from '../types/enums';

/** Sexo implícito de una categoría. null solo para TERNERO. */
export function getDefaultGenderForCategory(categoria?: string | null): string | null {
  switch (categoria) {
    case Enums.CATEGORIA.TORO:
    case Enums.CATEGORIA.NOVILLO:
    case Enums.CATEGORIA.MEJ: // "M.E.J" = Macho Entero Joven
      return Enums.SEXO.M;
    case Enums.CATEGORIA.VACA:
    case Enums.CATEGORIA.VAQUILLONA:
      return Enums.SEXO.H;
    case Enums.CATEGORIA.TERNERO:
    default:
      return null;
  }
}

/**
 * ¿El campo Sexo debe ser editable? Solo para TERNERO.
 * Para toda otra categoría el sexo es automático y NO seteable.
 */
export function shouldShowGenderField(categoria?: string | null): boolean {
  return categoria === Enums.CATEGORIA.TERNERO;
}

/**
 * Garantía de persistencia: asegura que un registro que se va a guardar
 * (animales_historico, del cual se regenera `animales` por trigger de DB)
 * lleve sexo si su categoría lo define.
 *
 * - No-TERNERO sin sexo → se le asigna el sexo de la categoría.
 * - TERNERO → puede quedar sin sexo (undefined).
 * - Si ya tiene sexo → se respeta (no se sobreescribe).
 *
 * Devuelve una COPIA; no muta el original. Si recibe null/undefined, lo devuelve tal cual.
 */
export function ensureSexoFromCategoria<T extends { categoria?: string | null; sexo?: string | null }>(
  record: T,
): T & { sexo?: string | null } {
  if (!record) return record;
  if (record.categoria && record.categoria !== Enums.CATEGORIA.TERNERO && !record.sexo) {
    const sexo = getDefaultGenderForCategory(record.categoria);
    if (sexo) return { ...record, sexo };
  }
  return record;
}
